import { useQuery } from "@tanstack/react-query";
import { createLogger } from "logging";
import { BoardSettingsModel } from "models/BoardSettingsModel";
import { PatchBoardModel } from "models/PatchBoardModel";
import { useApi, usePermissions, useToasts, useTranslations } from "providers";
import { useBoardSettings } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { EditBoardPropertiesPanel } from "./components/EditBoardPropertiesPanel";
import {
  EditBoardPropertiesContext,
  EditBoardPropertiesContextProps,
  EditBoardPropertiesFieldValues,
  getEditBoardPropertiesFieldValues,
} from "./EditBoardPropertiesContext";

const logger = createLogger("EditBoardPropertiesProvider");

export const EditBoardPropertiesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const { boardApi } = useApi();
  const i18n = useTranslations();
  const toasts = useToasts();
  const { boardId, boardSettings, reloadBoardSettings } = useBoardSettings();
  const beforeName = useMemo(() => boardSettings?.name ?? "", [boardSettings]);

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmUnsaved, setConfirmUnsaved] = useState(false);

  const form = useForm<EditBoardPropertiesFieldValues>({
    defaultValues: undefined,
  });
  const { formState, getValues, reset, setValue, control } = form;
  const [editBoardId, setEditBoardId] = useState<number>(-1);

  const { isDirty, isValid } = formState;
  const watchErrors = JSON.stringify(formState.errors, null, 2);
  const isSubmittable = useMemo(() => isValid || watchErrors == "{}", [isValid, watchErrors]);

  const businessOwner = useWatch({ control, name: "businessOwner" });

  // update organisationUnit from selected business owner
  useEffect(() => {
    if (!businessOwner) return;
    setValue("organisationUnit", businessOwner.organisationUnit ?? "");
  }, [businessOwner, setValue]);

  // open by setting edit id; the query will fetch fresh data and reset the form
  const openEditBoardProperties = useCallback((boardSettings: BoardSettingsModel) => {
    setEditBoardId(Number(boardSettings?.id ?? -1));
    setIsOpen(true);
  }, []);

  // Load board settings when an edit id is set
  const queryResult = useQuery({
    queryKey: [`Board|${editBoardId}|EditProperties`],
    queryFn: async ({ signal }) => {
      const data = await boardApi.getBoardSettings(editBoardId, signal);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: editBoardId > 0,
    refetchOnWindowFocus: false,
  });
  const { error } = queryResult;
  const isLoading = queryResult.isFetching;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  useEffect(() => {
    if (!queryResult.data) return;
    reset(getEditBoardPropertiesFieldValues(queryResult.data));
  }, [queryResult.data, reset]);

  const handleClose = useCallback(
    (ignoreDirty?: boolean) => {
      const confirm = isDirty && !ignoreDirty;
      logger.debug(`handleClose(): isDirty:=${isDirty} (ignore:=${ignoreDirty}) -> ${confirm}`);
      if (confirm) {
        setConfirmUnsaved(true);
      } else {
        reset();
        setIsOpen(false);
        setEditBoardId(-1);
        reloadBoardSettings();
      }
    },
    [isDirty, reset, reloadBoardSettings]
  );

  const getPatch = useCallback(
    (values: EditBoardPropertiesFieldValues) => {
      const patch: PatchBoardModel = {
        id: Number(boardId!),
        ...values,
        businessOwner: values.businessOwner?.upn,
      };
      return patch;
    },
    [boardId]
  );

  const handleSave = useCallback(async () => {
    logger.debug("handleSave():", isValid);
    if (!isValid) return;

    try {
      setIsSubmitting(true);

      const patch = getPatch(getValues());
      const afterName = patch.name ?? beforeName;

      try {
        await boardApi.updateBoard(patch);
        toasts.updateSuccess(i18n.Board, afterName);
        handleClose(true);
      } catch (error) {
        toasts.updateFailure(i18n.Board, beforeName, error);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [isValid, i18n, boardApi, toasts, getPatch, getValues, handleClose, beforeName]);

  const contextValue: EditBoardPropertiesContextProps = useMemo(
    () => ({
      confirmUnsaved,
      setConfirmUnsaved,
      isDirty,
      isValid,
      isLoading,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
      openEditBoardProperties,
    }),
    [
      confirmUnsaved,
      isDirty,
      isValid,
      isLoading,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
      openEditBoardProperties,
    ]
  );

  return (
    <EditBoardPropertiesContext.Provider value={contextValue}>
      {children}
      {isOpen && <EditBoardPropertiesPanel />}
    </EditBoardPropertiesContext.Provider>
  );
};
